import { PERSONAS, ALL_DEPTS } from "../data/org";
import { profileOf } from "../data/profiles";
import type { AgendaMode } from "../store/ui";

export interface ClaudeReq {
  apiKey: string;
  model: string;
  topic: string;
  mode: AgendaMode;
  personaIds: string[];
}

const ENDPOINT = "https://api.anthropic.com/v1/messages";

/** Router: mapeia complexidade → modelo Claude. */
export const COMPLEXITY_MODEL: Record<string, string> = {
  trivial:  "claude-haiku-4-5",
  low:      "claude-haiku-4-5",
  medium:   "claude-sonnet-4-5",
  high:     "claude-opus-4-7",
  critical: "claude-opus-4-7"
};

export function modelForComplexity(complexity: string | undefined, fallback: string): string {
  if (!complexity) return fallback;
  return COMPLEXITY_MODEL[complexity] ?? fallback;
}

/** Gera 10 ideias de criativo / hook pra mídias sociais. */
export async function generateSocialIdeasAI(opts: {
  apiKey: string;
  model: string;
  clientName: string;
  audience: string;
  voiceTone?: string;
  platform: string;
  format: "reel" | "carrossel" | "story" | "post" | "tiktok";
  goal?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Você é o Social Media Strategist Sênior da AGÊNCIA VPC (canalize Mr. Beast + Alex Cooper + Casimiro).

CLIENTE: ${opts.clientName}
PLATAFORMA: ${opts.platform}
FORMATO: ${opts.format}
PÚBLICO: ${opts.audience}
${opts.voiceTone ? `TOM: ${opts.voiceTone}` : ""}
${opts.goal ? `OBJETIVO: ${opts.goal}` : ""}

Gere 10 ideias DIFERENTES e ESPECÍFICAS, cada uma com:
- HOOK (3-5 segundos / primeira linha)
- IDEIA DO CONTEÚDO (1-2 linhas explicando)
- POR QUE PROVAVELMENTE VAI BOMBAR (1 linha — princípio viral usado)

Formato:

▸ IDEIA 01
HOOK: [...]
CONTEÚDO: [...]
POR QUÊ: [...]

(repete pra 10)

Sem rodeio. Direto. Em pt-BR.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 2000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Analisa um vídeo viral (referência) e extrai padrões aplicáveis. */
export async function analyzeViralAI(opts: {
  apiKey: string;
  model: string;
  videoUrlOrDescription: string;
  clientContext?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Você é analista de virais da AGÊNCIA VPC. O Marcio mandou esta referência:

REFERÊNCIA: ${opts.videoUrlOrDescription}
${opts.clientContext ? `CONTEXTO DO CLIENTE: ${opts.clientContext}` : ""}

Analise e devolva (em pt-BR, conciso):

▸ ESTRUTURA DO VÍDEO
[abertura/hook, desenvolvimento, payoff/clímax, CTA — em bullets]

▸ TÉCNICAS USADAS
[3-5 técnicas: pattern interrupt, loop, escalada, payoff antecipado, etc.]

▸ POR QUE VIRALIZOU
[2-3 linhas]

▸ COMO ADAPTAR PRO CLIENTE
[3 ideias específicas de como replicar o padrão pra esta marca/produto]

Seja direto, prático.`;
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Sugere melhores horários de postagem pro público do cliente. */
export async function suggestBestTimesAI(opts: {
  apiKey: string;
  model: string;
  clientName: string;
  audience: string;
  platform: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Analista de social. Sugira os 5 MELHORES horários (dia da semana + hora BR) pra postar.

CLIENTE: ${opts.clientName}
PÚBLICO: ${opts.audience}
PLATAFORMA: ${opts.platform}

Considere fuso BR, padrão de consumo do público específico (não genérico), e formatos típicos.

Formato:
▸ TOP 5 JANELAS
1. dia HHh - HHh — [justificativa em 1 linha]
2. ...

▸ EVITAR
[1-2 janelas a evitar com motivo]

▸ RITMO IDEAL
[ex: 3-5 posts/semana, com 1 reel/dia, etc — específico pra este público]`;
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 800,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Estimar complexidade de uma demanda via Haiku (chamada barata). */
export async function estimateComplexityAI(opts: {
  apiKey: string;
  title: string;
  description: string;
  targetDepts: string[];
}): Promise<{ complexity: string; reason: string }> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Classifique a complexidade desta demanda em UMA palavra entre: trivial, low, medium, high, critical.

- trivial: tarefa repetitiva, <1h, sem decisão (ex: ajustar texto, trocar cor)
- low: tarefa clara com escopo curto, 1-4h (ex: post simples, bug fix)
- medium: criativa ou estratégica padrão, dia de trabalho (ex: VSL, campanha simples)
- high: estratégica complexa, multi-departamento (ex: lançamento, reposicionamento)
- critical: decisão de alto risco, irreversível (ex: contrato M&A, rebranding completo)

DEMANDA:
título: ${opts.title}
descrição: ${opts.description}
departamentos-alvo: ${opts.targetDepts.join(", ")}

Retorne APENAS JSON: {"complexity": "...", "reason": "1 frase curta"}`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: "claude-haiku-4-5",
      max_tokens: 200,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  try {
    const obj = JSON.parse(m ? m[0] : text);
    return { complexity: obj.complexity || "medium", reason: obj.reason || "" };
  } catch {
    return { complexity: "medium", reason: "fallback" };
  }
}

const buildPrompt = (req: ClaudeReq) => {
  const personas = req.personaIds
    .map(id => PERSONAS.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const dept = (slot: string) => {
    const [d] = slot.split(":");
    return ALL_DEPTS.find(x => x.id === d)?.name ?? d;
  };

  const cast = personas
    .map(p => {
      const prof = profileOf(p.id, p.tier);
      return `- ${p.name} (${dept(p.slotId)}, poder ${p.power}/10)
   bio: ${p.bio}
   habilidades: ${prof.skills.join(", ")}
   expectativa: ${prof.expectations}
   voz: ${prof.voice}`;
    })
    .join("\n\n");

  if (req.mode === "conselho") {
    return `Você é o roteirista da AGÊNCIA VPC, uma holding premium fictícia comandada por Marcio.
Marcio precisa de conselho dos profissionais abaixo para decidir sobre o seguinte tópico:

TÓPICO: "${req.topic}"

CONSELHEIROS À MESA:
${cast}

Gere uma "ata de conselho" em português do Brasil, dirigida AO MARCIO em segunda pessoa.
Cada conselheiro deve dar 1 conselho curto, em UMA frase, no TOM CARACTERÍSTICO dele (Trump é direto e bombástico, Buffett é paciente e analítico, Hormozi é agressivo em ofertas, Ogilvy fala em headline, Sobral é dado, etc).

Formato exato:
▸ NOME DO CONSELHEIRO
   Marcio, [conselho direto, 1 frase, voz característica].

(linha em branco entre cada um)

Ao final adicione:
▸ SÍNTESE
   [parágrafo de 2-3 linhas sintetizando o consenso e a tensão entre conselhos]
${personas.find(p => p.id === "trump") ? "   Trump bate o martelo: [veredito plausível dado os conselhos]." : "   (Trump ausente — decisão sem a última canetada.)"}

Seja conciso, direto, sem rodeios. Não invente conselheiros que não estão na lista.`;
  }

  // mode === ata
  return `Você é o roteirista da AGÊNCIA VPC, uma holding premium fictícia comandada por Marcio (consigliere) e Trump (decisão final).
Os profissionais abaixo estão reunidos para discutir o seguinte tópico:

TÓPICO: "${req.topic}"

PARTICIPANTES:
${cast}

Gere uma "ata de reunião" em português do Brasil. Cada participante FALA sobre o tópico no SEU TOM CARACTERÍSTICO (1 frase forte cada).

Agrupe por DEPARTAMENTO. Formato:

Pauta: ${req.topic}
[1 linha de contexto]

▸ NOME DO DEPARTAMENTO
   — Fulano: [fala em 1 frase, tom característico]
   — Beltrano: [fala em 1 frase, tom característico]

(repita para cada departamento presente)

▸ DECISÃO FINAL
${personas.find(p => p.id === "trump") ? `   — ${personas.find(p => p.id === "marcio") ? "Marcio (consigliere) sussurra o veredito ao Trump." : "Sem Marcio — Trump decide no instinto."}
   — Trump bate o martelo: [veredito plausível, em 1 frase].` : "   — Sem Trump na sala, ninguém fecha hoje."}

Seja conciso. Não invente personagens fora da lista. Mantenha as falas em português.`;
};

/** Recomenda quem deveria estar numa reunião sobre dado assunto. */
export async function suggestMeetingAttendeesAI(opts: {
  apiKey: string;
  model: string;
  topic: string;
  mode: "ata" | "conselho";
  hiredAgentIds: string[];
  /** opcional — descrição extra ou cliente */
  context?: string;
}): Promise<{ personaId: string; reason: string }[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const agents = opts.hiredAgentIds
    .map(id => PERSONAS.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map(p => {
      const prof = profileOf(p.id, p.tier);
      const [d] = p.slotId.split(":");
      return `- id=${p.id} | ${p.name} | dept=${d} | skills=[${prof.skills.join(", ")}] | voz="${prof.voice}"`;
    }).join("\n");

  const MODE_HINT = opts.mode === "conselho"
    ? "É um CONSELHO ao Marcio — convoque conselheiros estratégicos e líderes seniores que dão diretriz. " +
      "Sempre inclua o Trump se contratado (voto final). Marcio (consigliere) sempre dentro se contratado."
    : "É uma ATA de reunião operacional — convoque quem EXECUTA o tópico (especialistas/supervisores dos departamentos relevantes). " +
      "Inclua 1 representante do conselho se a decisão exige aprovação alta.";

  const prompt =
`Você é o secretário executivo da AGÊNCIA VPC. O Marcio quer convocar uma reunião e precisa que você indique QUEM CHAMAR do time contratado.

ASSUNTO: "${opts.topic}"
TIPO: ${opts.mode === "conselho" ? "conselho ao Marcio (estratégico)" : "ata operacional (execução)"}
${opts.context ? `CONTEXTO: ${opts.context}` : ""}

INSTRUÇÃO: ${MODE_HINT}

TIME CONTRATADO DISPONÍVEL:
${agents}

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra) com 3 a 7 participantes:
[{"personaId": "id-exato-da-lista", "reason": "motivo curto em 1 frase"}]

Regras:
- Só IDs da lista acima (não invente)
- Priorize variedade de departamentos quando relevante
- Conselho (Trump, Marcio, advisors) só pra decisão estratégica
- Não convoque mais que 7 — reunião grande não fecha
- Ordene da pessoa mais central pra mais auxiliar`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 800,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  const match = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(match ? match[0] : text);
    if (Array.isArray(arr)) return arr.filter(x => x?.personaId);
  } catch { /* fallthrough */ }
  return [];
}

/** Pede ao Claude que recomende quais agentes designar pra uma demanda. */
export async function suggestAgentsAI(opts: {
  apiKey: string;
  model: string;
  title: string;
  description: string;
  targetDepts: string[];
  hiredAgentIds: string[];
}): Promise<{ personaId: string; reason: string }[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const agents = opts.hiredAgentIds
    .map(id => PERSONAS.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .map(p => {
      const prof = profileOf(p.id, p.tier);
      const [d] = p.slotId.split(":");
      return `- id=${p.id} | ${p.name} | dept=${d} | skills=[${prof.skills.join(", ")}] | voz="${prof.voice}"`;
    }).join("\n");

  const prompt =
`Você é o supervisor de RH da AGÊNCIA VPC. Marcio criou a demanda abaixo e precisa que você indique os MELHORES agentes do time atual pra executar.

DEMANDA:
- título: ${opts.title}
- descrição: ${opts.description}
- departamentos-alvo: ${opts.targetDepts.join(", ") || "qualquer"}

AGENTES CONTRATADOS DISPONÍVEIS:
${agents}

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra) no formato:
[{"personaId": "id-exato-da-lista", "reason": "motivo curto em 1 frase"}]

Recomende entre 1 e 4 agentes. Priorize quem está nos departamentos-alvo e tem skills aderentes. Não invente IDs fora da lista.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-6",
      max_tokens: 600,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  // tenta extrair primeiro bloco JSON
  const match = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(match ? match[0] : text);
    if (Array.isArray(arr)) return arr.filter(x => x?.personaId);
  } catch { /* fallthrough */ }
  return [];
}

/** Extrai ações concretas (tarefas) de uma ata de reunião. */
export async function extractTasksAI(opts: {
  apiKey: string;
  model: string;
  topic: string;
  agenda: string;
  clientHint?: string;
}): Promise<{ title: string; description: string; targetDepts: string[]; priority: string }[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const prompt =
`Você é o supervisor de operações da AGÊNCIA VPC. Acabou de terminar uma reunião com o seguinte conteúdo:

TÓPICO: ${opts.topic}
${opts.clientHint ? `CLIENTE: ${opts.clientHint}` : ""}

ATA:
${opts.agenda}

Extraia AÇÕES CONCRETAS desta reunião — coisas que precisam virar demanda no quadro de trabalho.
Cada ação deve ter: título curto, descrição (1-2 linhas), departamentos-alvo entre [copy, criativo, video, trafego, vendas, juridico, financeiro, endomarketing], prioridade [low/normal/high/urgent].

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra):
[{"title": "…", "description": "…", "targetDepts": ["copy"], "priority": "normal"}]

De 2 a 6 tarefas. Se a ata for vaga, gere menos. Se for rica, gere mais. Use português direto.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-6",
      max_tokens: 1200,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  const match = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(match ? match[0] : text);
    if (Array.isArray(arr)) return arr.filter(x => x?.title);
  } catch { /* fallthrough */ }
  return [];
}

/** Estrutura de um slide. */
export interface SlideSpec {
  title: string;
  subtitle?: string;
  /** bullets ou parágrafo curto */
  bullets?: string[];
  body?: string;
  /** prompt pra gerar imagem ilustrativa pro slide (opcional) */
  imagePrompt?: string;
  /** layout sugerido */
  layout?: "cover" | "section" | "bullets" | "image-left" | "image-right" | "quote" | "metric" | "closing";
  /** se for metric: número + caption */
  metric?: { value: string; caption: string };
  /** se for quote: texto + autor */
  quote?: { text: string; author: string };
}

/** Gera legenda otimizada pra Instagram baseada no deck + brand. */
export async function generateIGCaptionAI(opts: {
  apiKey: string;
  model: string;
  deckTitle: string;
  deckSubtitle?: string;
  slides: { title: string; bullets?: string[]; body?: string; metric?: any; quote?: any }[];
  brandHandle: string;        // @cliente
  brandName: string;
  voiceTone?: string;
  audience: string;
  clientTagsCsv?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const slidesBrief = opts.slides.slice(0, 10).map((s, i) =>
    `${i+1}. ${s.title}${s.body ? " — " + s.body.slice(0, 80) : ""}${s.bullets?.length ? " | " + s.bullets.slice(0,2).join(" / ") : ""}`
  ).join("\n");

  const prompt =
`Você é o estrategista de social da AGÊNCIA VPC. Escreva a LEGENDA perfeita pro Instagram desse carrossel.

CARROSSEL:
título: ${opts.deckTitle}
${opts.deckSubtitle ? `subtítulo: ${opts.deckSubtitle}` : ""}
slides:
${slidesBrief}

MARCA: ${opts.brandName} (${opts.brandHandle})
PÚBLICO: ${opts.audience}
${opts.voiceTone ? `TOM DE VOZ: ${opts.voiceTone}` : ""}

ESTRUTURA DA LEGENDA (siga rigoroso):

1. HOOK (linha 1) — 1 frase forte que para o scroll. SEM emoji no começo.
2. (linha em branco)
3. CORPO — 3-5 linhas/parágrafos curtos. Use micro-pausa: ".\n.\n." só uma vez no meio se fizer sentido (não obrigatório). Conecta com o carrossel sem leitura literal.
4. CTA — 1 linha clara (ex: "Salva esse post.", "Manda pro amigo que precisa.", "Link na bio."). Pode usar emoji forte ✦◉▶.
5. (linha em branco)
6. HASHTAGS — 8-15 hashtags relevantes, agrupadas em uma só linha. Misture genéricas (#marketing) + nicho (#mentoriadeempresarios) + marca (#${opts.brandHandle.replace("@","")}).

REGRAS:
- Máx 2000 caracteres (margem).
- Pt-BR, FALADO, sem corporativismo.
- Não cite "slides", "carrossel", "arrasta pro lado" — soa amador.
- Faça sentir que A PESSOA ESCREVEU, não bot.

Retorne APENAS o texto da legenda — sem markdown, sem aspas, sem rótulos.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

export interface ArtDirection {
  /** estilo fotográfico macro (1 frase forte) */
  photoStyle: string;
  /** logo: onde, quanto, opacidade */
  logoUsage: { include: boolean; slides: number[]; placement: "corner" | "bottom" | "centered" | "watermark"; opacity: number };
  /** spokesperson */
  spokespersonUsage: { include: boolean; slides: number[]; angle: string; mood: string };
  /** paleta no creative — quanto inundar vs acentos */
  paletteIntensity: "minimal-accent" | "balanced" | "saturated" | "monochromatic";
  /** estética dominante: editorial, documental, lifestyle, brutalist, magazine */
  aesthetic: string;
  /** mood word — 1 palavra */
  mood: string;
  /** o que NÃO fazer */
  avoid: string[];
  /** justificativa do diretor de arte */
  rationale: string;
}

export interface SlideCritique {
  slideIndex: number;        // 1-based
  score: number;             // 0-10
  decision: "approve" | "regenerate" | "kill";
  critique: string;          // o que tá errado / certo (1-2 frases)
  fixHint?: string;          // o que mudar especificamente (se regenerate)
}

export interface DeckCritique {
  overallScore: number;
  monotony: number;          // 0-10 — quanto monotonia/repetição
  paletteIssue: string | null;
  layoutIssue: string | null;
  slides: SlideCritique[];
  paulaVerdict: string;      // 2-3 linhas — Paula Scher fala direto
}

/** Paula Scher CRITICA o deck gerado — nota + decisão por slide. */
export async function criticizeDeckAI(opts: {
  apiKey: string;
  model: string;
  deck: { title: string; slides: Array<{ title: string; layout?: string; subtitle?: string; bullets?: string[]; body?: string; metric?: any; quote?: any; imagePrompt?: string }> };
  hasImages: boolean[];      // por slide — se Gemini gerou imagem
  clientContext?: string;
}): Promise<DeckCritique> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const slidesBlock = opts.deck.slides.map((s, i) =>
    `${i + 1}. layout=${s.layout ?? "bullets"} | imagem=${opts.hasImages[i] ? "✓" : "✗"} | título="${s.title}"`
    + (s.subtitle ? ` | sub="${s.subtitle}"` : "")
    + (s.bullets?.length ? ` | bullets=[${s.bullets.slice(0,3).join(" / ")}]` : "")
    + (s.metric ? ` | metric=${s.metric.value} ${s.metric.caption}` : "")
    + (s.quote ? ` | quote="${s.quote.text?.slice(0,60)}"` : "")
    + (s.imagePrompt ? ` | imagePrompt="${s.imagePrompt.slice(0,100)}…"` : "")
  ).join("\n");

  const prompt =
`Você é PAULA SCHER (diretora de arte sênior, Pentagram). Marcio acabou de te mandar este deck e quer SUA opinião BRUTAL — você é conhecida por não passar a mão na cabeça.

Critique cada slide com NOTA 0-10 e decisão.

DECK: ${opts.deck.title}
${opts.clientContext ? `CLIENTE: ${opts.clientContext}` : ""}

SLIDES:
${slidesBlock}

CRITÉRIOS:
- **Variedade**: tem layouts repetidos demais? Tudo é metric/bullets?
- **Hierarquia tipográfica**: títulos parecem ter peso e drama?
- **Paleta**: tá monocromática chata ou tem respiro?
- **Conceito**: cada slide tem RAZÃO de existir ou é enchimento?
- **Imagem vs Texto**: balanceamento?
- **Cover/Closing**: têm o impacto necessário?
- **Repetição**: 2+ slides falando a mesma coisa de jeito parecido?

Para cada slide, decide:
- "approve" (nota ≥ 7): tá bom, segue
- "regenerate" (nota 4-6): refazer com fix específico — dê o fixHint
- "kill" (nota ≤ 3): tirar do deck, não vale regerar

Para o deck inteiro:
- overallScore: 0-10
- monotony: 0-10 (0 = bem variado, 10 = tudo igual)
- paletteIssue: 1 frase OU null se tá ok
- layoutIssue: 1 frase OU null se tá ok
- paulaVerdict: 2-3 linhas BRUTAIS, primeira pessoa, ela falando direto

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra):

{
  "overallScore": 6,
  "monotony": 7,
  "paletteIssue": "...",
  "layoutIssue": "...",
  "paulaVerdict": "...",
  "slides": [
    { "slideIndex": 1, "score": 8, "decision": "approve", "critique": "..." },
    { "slideIndex": 2, "score": 5, "decision": "regenerate", "critique": "...", "fixHint": "..." }
  ]
}

Seja DURA. Se tá 5, é 5. Se tá 3, não tente salvar. O Marcio precisa ouvir a verdade.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 2500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  try {
    const obj = JSON.parse(m ? m[0] : text);
    return obj as DeckCritique;
  } catch (e) {
    throw new Error("Resposta da crítica não é JSON válido");
  }
}

/** Direção de Arte: decide identidade visual ANTES de gerar imagens. */
export async function directArtAI(opts: {
  apiKey: string;
  model: string;
  taskTitle: string;
  taskDescription: string;
  hasLogo: boolean;
  hasSpokesperson: boolean;
  clientContext?: string;
  numSlides: number;
}): Promise<ArtDirection> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const prompt =
`Você é Paula Scher (diretora de arte sênior da AGÊNCIA VPC). Marcio reclamou que os criativos estão saindo medianos. Você vai decidir A DIREÇÃO VISUAL antes de gerar qualquer imagem.

DEMANDA: ${opts.taskTitle}
BRIEFING: ${opts.taskDescription}
${opts.clientContext ? `CLIENTE: ${opts.clientContext}` : ""}
RECURSOS DISPONÍVEIS:
- Logo da marca: ${opts.hasLogo ? "SIM (pode embarcar nas imagens)" : "NÃO disponível"}
- Garoto/garota propaganda: ${opts.hasSpokesperson ? "SIM (foto disponível como referência)" : "NÃO disponível"}
- Total de slides: ${opts.numSlides}

DECIDA com convicção (estilo Pentagram — opiniões fortes, defendidas com argumento):

1. ESTILO FOTOGRÁFICO macro (1 frase forte). Ex: "fotografia editorial documental, filme 35mm push-process, mood Magnum agency"
2. LOGO: incluir ou não? Em quais slides especificamente? Onde (canto/rodapé/centrado/watermark)? Opacidade?
3. SPOKESPERSON: usar em quais slides? Que ângulo, mood, papel?
4. PALETA: quão presente? (minimal-accent = só toques · balanced · saturated = paleta forte · monochromatic = quase mono)
5. ESTÉTICA dominante: editorial / documental / lifestyle / brutalist / magazine
6. MOOD: 1 palavra (ex: "tenso", "promissor", "luxo-discreto", "urgente", "contemplativo")
7. AVOID: 3-5 anti-padrões específicos (ex: "evitar over-saturação", "sem texto na imagem", "sem stock genérico de óculos escuros")
8. RATIONALE: 2-3 linhas defendendo essa direção pra ESTA demanda específica

Retorne APENAS JSON válido (sem markdown):

{
  "photoStyle": "...",
  "logoUsage": { "include": true, "slides": [1, ${opts.numSlides}], "placement": "corner", "opacity": 0.8 },
  "spokespersonUsage": { "include": true, "slides": [1, 3], "angle": "...", "mood": "..." },
  "paletteIntensity": "balanced",
  "aesthetic": "editorial",
  "mood": "...",
  "avoid": ["...", "..."],
  "rationale": "..."
}

OPINE com força. Se faz sentido NÃO usar logo, diga não. Se faz sentido usar o spokesperson em todos, diga em todos. Direção forte > direção tímida.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  try {
    const obj = JSON.parse(m ? m[0] : text);
    return obj as ArtDirection;
  } catch (e) {
    throw new Error("Resposta da direção de arte não é JSON válido");
  }
}

/** Gera roteiros de narração (1 por slide) — fala do apresentador. */
export async function generateNarrationAI(opts: {
  apiKey: string;
  model: string;
  deck: { title: string; slides: { title: string; subtitle?: string; bullets?: string[]; body?: string; metric?: any; quote?: any; layout?: string }[] };
  presenterName: string;
  presenterVoice: string;
  clientContext?: string;
}): Promise<string[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const slidesBlock = opts.deck.slides.map((s, i) => {
    const parts = [`${i+1}. layout=${s.layout ?? "bullets"} | título="${s.title}"`];
    if (s.subtitle) parts.push(`subtitle=${s.subtitle}`);
    if (s.body)     parts.push(`body=${s.body.slice(0, 200)}`);
    if (s.bullets?.length) parts.push(`bullets=[${s.bullets.join(" / ")}]`);
    if (s.metric)   parts.push(`metric=${s.metric.value} ${s.metric.caption}`);
    if (s.quote)    parts.push(`quote="${s.quote.text}" — ${s.quote.author}`);
    return parts.join(" | ");
  }).join("\n");

  const prompt =
`Você é ${opts.presenterName} apresentando o deck "${opts.deck.title}" pra uma audiência ao vivo.

VOZ CARACTERÍSTICA: ${opts.presenterVoice}
${opts.clientContext ? `CONTEXTO: ${opts.clientContext}` : ""}

SLIDES DA APRESENTAÇÃO:
${slidesBlock}

Gere a NARRAÇÃO falada de cada slide (o que você diria com a boca, não o que está escrito na tela).

REGRAS:
1. Cada narração: 30-60 segundos lidos em voz natural (~100-180 palavras).
2. Pt-BR FALADO. NÃO use markdown, não cite "slide 3", "próximo slide". É fala continua.
3. Mantenha a VOZ característica do apresentador (${opts.presenterName}).
4. A narração CONECTA com o slide anterior — fluxo natural.
5. NÃO leia o slide — EXPANDA. O slide é o esqueleto, a fala é a carne.
6. Termine a narração de cada slide com uma transição/gancho que segura atenção.

RETORNE APENAS JSON ARRAY de strings (sem markdown, sem texto extra), uma string por slide na ordem:
["narração do slide 1...", "narração do slide 2...", ...]

Exatamente ${opts.deck.slides.length} strings.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 4000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  const m = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(m ? m[0] : text);
    if (Array.isArray(arr)) return arr.map(String);
  } catch { /* fallthrough */ }
  return opts.deck.slides.map(() => "");
}

export interface DeckSpec {
  title: string;
  subtitle?: string;
  brandPrimary?: string;
  brandSecondary?: string;
  brandAccent?: string;
  slides: SlideSpec[];
}

/** Gera uma apresentação estruturada (JSON) pra uma demanda. */
export async function generateDeckAI(opts: {
  apiKey: string;
  model: string;
  taskTitle: string;
  taskDescription: string;
  agentName: string;
  agentVoice: string;
  clientContext?: string;
  numSlides?: number;
}): Promise<DeckSpec> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const N = opts.numSlides ?? 8;

  const prompt =
`Você é ${opts.agentName} (${opts.agentVoice}) da AGÊNCIA VPC. Crie uma APRESENTAÇÃO completa pra esta demanda.

DEMANDA: ${opts.taskTitle}
BRIEFING: ${opts.taskDescription}
${opts.clientContext ? `CLIENTE: ${opts.clientContext}` : ""}

Crie ${N} slides em JSON. Cada slide com layout APROPRIADO pra mensagem (NÃO repetir o mesmo layout slide após slide).

LAYOUTS DISPONÍVEIS (rotacione):
- "cover": abertura — title + subtitle + imagePrompt
- "section": divisor de seção
- "bullets": título + 3-5 bullets curtos
- "image-left" / "image-right": imagem grande lateral + texto
- "quote": frase forte + autor
- "metric": número grande + caption
- "closing": chamada final + próximos passos

REGRAS DE VARIEDADE — CRÍTICAS:
1. Slide 1 = "cover" SEMPRE
2. Último slide = "closing" SEMPRE
3. Use NO MÁXIMO 2 slides do mesmo layout em todo o deck
4. Misture: pelo menos 1 quote OR section, pelo menos 1 metric, pelo menos 2 image-left/right COM imagePrompt detalhado
5. Image prompts ricos (60-100 palavras), CADA UM com cenário diferente (não tudo carro/escritório)
6. Bullets ≤ 12 palavras cada
7. Variação de drama: alterne slides "denso de info" com slides "respiro" (section/quote)

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra):

{
  "title": "Título da apresentação (curto e forte)",
  "subtitle": "Subtítulo de 1 linha",
  "slides": [
    { "layout": "cover", "title": "...", "subtitle": "...", "imagePrompt": "..." },
    { "layout": "bullets", "title": "...", "bullets": ["...", "..."] },
    { "layout": "image-right", "title": "...", "body": "...", "imagePrompt": "..." },
    { "layout": "metric", "title": "...", "metric": { "value": "10x", "caption": "..." } },
    { "layout": "quote", "quote": { "text": "...", "author": "..." } },
    { "layout": "section", "title": "..." },
    { "layout": "closing", "title": "...", "bullets": ["Passo 1", "Passo 2"] }
  ]
}

Tom: na voz do ${opts.agentName}. Direto, premium, pt-BR. Não invente layouts fora da lista.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 4000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  try {
    const obj = JSON.parse(m ? m[0] : text);
    if (!Array.isArray(obj.slides)) throw new Error("sem slides[]");
    return obj as DeckSpec;
  } catch (e) {
    throw new Error("Resposta da IA não é JSON válido: " + (e as Error).message);
  }
}

/** Marcio (consigliere) concilia 2+ outputs divergentes em uma síntese única. */
export async function mediateConflictAI(opts: {
  apiKey: string;
  model: string;
  topic: string;
  outputs: { by: string; text: string }[];
  clientContext?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  if (opts.outputs.length < 2) throw new Error("Conciliação exige ≥2 outputs.");

  const block = opts.outputs
    .map((o, i) => `▸ VERSÃO ${i + 1} — por ${o.by}:\n${o.text}\n`)
    .join("\n");

  const prompt =
`Você é o MARCIO (consigliere da AGÊNCIA VPC). Conhece negócio profundamente, sabe filtrar opinião forte e tem decisão calma e estratégica.

O time entregou versões DIFERENTES pra esta demanda:

DEMANDA: ${opts.topic}
${opts.clientContext ? `CONTEXTO: ${opts.clientContext}` : ""}

${block}

Sua tarefa: CONCILIAR essas versões numa síntese ÚNICA que pegue o melhor de cada uma, resolvendo as tensões. Não é eleger uma — é integrar.

Formato:

▸ DIAGNÓSTICO (1-2 frases)
Onde cada versão acerta e onde diverge.

▸ DECISÃO CONCILIADA
[a entrega final unificada, na voz e formato apropriado pro tipo de output]

▸ JUSTIFICATIVA (2-3 linhas)
Por que essa síntese é melhor que cada versão isolada.

Direto, em pt-BR, sem rodeio.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 2000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Trump dá o veredito final — voto de Minerva. */
export async function tiebreakerAI(opts: {
  apiKey: string;
  model: string;
  topic: string;
  outputs: { by: string; text: string }[];
  marcioMediation?: string;
  clientContext?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const block = opts.outputs
    .map((o, i) => `▸ VERSÃO ${i + 1} — ${o.by}:\n${o.text.slice(0, 800)}\n`)
    .join("\n");

  const prompt =
`Você é o DONALD TRUMP. Marcio te trouxe um deadlock pra você bater o martelo.

DEMANDA: ${opts.topic}
${opts.clientContext ? `CONTEXTO: ${opts.clientContext}` : ""}

VERSÕES NA MESA:
${block}

${opts.marcioMediation ? `MARCIO SUSSURROU NO SEU OUVIDO:\n${opts.marcioMediation}\n` : ""}

Você é a ÚLTIMA CANETADA. Decide AGORA. Voz característica: direto, bombástico, frases curtas em CAPS quando enfatiza, em pt-BR (Trump fala português aqui).

Formato:

▸ VEREDITO (1-2 linhas em CAPS quando precisar)
[qual versão vai, ou qual mistura, ou jogue fora e refaça]

▸ ORDEM EXECUTIVA
[1-3 ações concretas pra acontecer AGORA]

▸ ASSINATURA
"BATIDO. — D. TRUMP"

Sem floreio, sem rodeio, sem "ambos os lados". Bata o martelo.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-opus-4-7",  // Trump merece Opus
      max_tokens: 800,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Gera um system prompt customizado pra um agente baseado no perfil. */
export async function generateAgentSystemPromptAI(opts: {
  apiKey: string;
  model: string;
  personaName: string;
  personaBio: string;
  skills: string[];
  voice: string;
  expectations: string;
  deliverables: string[];
  hint?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Crie um SYSTEM PROMPT detalhado pra um agente de IA que vai EMBODIAR ${opts.personaName} em uma agência de marketing.

DADOS DO AGENTE:
- Nome: ${opts.personaName}
- Bio: ${opts.personaBio}
- Skills: ${opts.skills.join(", ")}
- Voz/tom: ${opts.voice}
- Expectativa: ${opts.expectations}
- Entregáveis típicos: ${opts.deliverables.join(", ")}
${opts.hint ? `- Direcionamento adicional: ${opts.hint}` : ""}

O prompt deve incluir, em pt-BR direto:
1. **Identidade**: quem é, contexto histórico/profissional
2. **Voz**: como fala, vocabulário típico, bordões se houver
3. **Skills aplicadas**: o que SABE fazer melhor
4. **Princípios**: o que nunca aceita / o que defende
5. **Formato de entrega**: como estrutura saída (lista, roteiro, etc)
6. **Anti-padrões**: o que NÃO fazer

Retorne APENAS o system prompt em si — sem rótulos extras, sem markdown bloated. Máx 400 palavras.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1200,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

export interface DiscussionTurn {
  by: string;          // personaId
  kind: "question" | "proposal" | "objection" | "agreement" | "synthesis";
  text: string;
}

/** Mesa-redonda: agentes discutem o briefing ANTES de executar. */
export async function agentDiscussionAI(opts: {
  apiKey: string;
  model: string;
  taskTitle: string;
  taskDescription: string;
  participants: { id: string; name: string; voice: string; skills: string[] }[];
  clientContext?: string;
}): Promise<DiscussionTurn[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const cast = opts.participants
    .map(p => `- id="${p.id}" | ${p.name} | voz="${p.voice}" | skills=[${p.skills.join(", ")}]`)
    .join("\n");

  const prompt =
`Você é o roteirista de uma MESA-REDONDA na AGÊNCIA VPC. Marcio acabou de apresentar uma demanda e os agentes do time vão DISCUTIR antes de executar — fazendo perguntas, propondo ângulos, objeção e por fim chegando em uma direção.

DEMANDA: ${opts.taskTitle}
BRIEFING: ${opts.taskDescription}
${opts.clientContext ? `CLIENTE: ${opts.clientContext}` : ""}

PARTICIPANTES NA MESA:
${cast}

Gere 6 a 12 turnos de discussão. Cada turno é uma fala de UM dos participantes acima.
TIPOS de turno:
- "question"   → pergunta provocativa sobre o briefing (faltam dados? público? prazo? métrica?)
- "proposal"   → propõe um ângulo/ideia concreta
- "objection"  → discorda, aponta risco, contradiz alguém
- "agreement"  → concorda e amplia a ideia de outro
- "synthesis"  → costura tudo em uma direção tática (geralmente perto do fim)

Regras:
1. Cada participante deve falar ao menos 1 vez.
2. Cada turno deve REFERENCIAR algo específico do briefing OU de outro turno (não pode ser monólogo paralelo).
3. Voz característica de cada um deve aparecer (Trump bombástico, Halbert irreverente, Sobral em dados, Hormozi em oferta, etc).
4. Pt-BR direto, 1-2 frases por turno, sem rodeio.
5. Fechar com 1-2 turnos de "synthesis" alinhando direção.

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra):
[
  { "by": "id-exato-da-lista", "kind": "question|proposal|objection|agreement|synthesis", "text": "..." }
]`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 2500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  const m = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(m ? m[0] : text);
    if (Array.isArray(arr)) return arr.filter(x => x?.by && x?.text);
  } catch { /* fallthrough */ }
  return [];
}

export interface FunnelNode {
  id: string;
  stage: "awareness" | "interest" | "consideration" | "decision" | "retention" | "advocacy";
  title: string;
  channel?: string;           // ex: "Reel Instagram", "Email 3", "Landing page", "Webinar"
  hook?: string;
  cta?: string;
  metric?: string;            // ex: "CTR 5%", "CPL R$3"
  notes?: string;
}

export interface FunnelSpec {
  title: string;
  description: string;
  goal: string;                // ex: "vender Mentoria VPC a R$ 5.000"
  primaryMetric: string;       // ex: "matrículas/mês"
  nodes: FunnelNode[];
}

/** Gera funil de vendas completo (mapa mental) via Claude. */
export async function generateFunnelAI(opts: {
  apiKey: string;
  model: string;
  brief: string;
  clientName?: string;
  clientPitch?: string;
  audience?: string;
  goal?: string;
  voice?: "brasao" | "thiagoroas" | "jebblount" | "balanced";
}): Promise<FunnelSpec> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const VOICE_INTRO: Record<NonNullable<typeof opts.voice>, string> = {
    brasao: "Você é FERNANDO BRASÃO — funnel hacker BR. CPL baixo + alta conversão. Funis pra infoproduto/SaaS.",
    thiagoroas: "Você é THIAGO ROAS — engenheiro de tráfego pago. Mede tudo. Funis com atribuição multi-touch.",
    jebblount: "Você é JEB BLOUNT — Fanatical Prospecting. Funis B2B com cadência outbound disciplinada.",
    balanced: "Você é um time de funnel hackers misturado: Brasão + Thiago Roas + Jeb Blount."
  };

  const prompt =
`${VOICE_INTRO[opts.voice ?? "balanced"]}

Desenhe um FUNIL DE VENDAS completo pra esta situação. Retorne como JSON com nós espaciais.

CONTEXTO:
- briefing: ${opts.brief}
${opts.clientName ? `- cliente: ${opts.clientName}` : ""}
${opts.clientPitch ? `- pitch: ${opts.clientPitch}` : ""}
${opts.audience ? `- público: ${opts.audience}` : ""}
${opts.goal ? `- objetivo: ${opts.goal}` : ""}

ESTÁGIOS (use TODOS):
- "awareness": topo de funil — conteúdo de descoberta (3-5 nós)
- "interest": meio — lead magnet, captura (2-4 nós)
- "consideration": nutrição — emails, retargeting, prova social (3-5 nós)
- "decision": fundo — VSL, oferta, fechamento (2-4 nós)
- "retention": pós-compra — onboarding, upsell (1-3 nós)
- "advocacy": indicação — programa de referência, social proof (1-2 nós)

Pra cada nó, especifique:
- "title": nome curto do componente (≤ 6 palavras)
- "channel": canal/formato concreto (ex: "Reel TikTok 30s", "Email 2/5", "Landing /aula1", "Live webinar", "DM automático")
- "hook": frase de gancho/headline (1 linha)
- "cta": chamada pra ação (1 linha)
- "metric": KPI esperado (ex: "CPV R$0.30", "CTR landing 8%", "Taxa show-up 40%")
- "notes": observação tática (1 frase)

RETORNE APENAS JSON VÁLIDO:

{
  "title": "Funil — <produto/cliente>",
  "description": "1-2 linhas resumindo a jornada",
  "goal": "${opts.goal ?? "<inferir>"}",
  "primaryMetric": "métrica principal (ex: matrículas/mês)",
  "nodes": [
    { "id": "a1", "stage": "awareness", "title": "...", "channel": "...", "hook": "...", "cta": "...", "metric": "...", "notes": "..." }
  ]
}

Total: 12-18 nós. Seja específico, cite formato e KPI esperado.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 3500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  const obj = JSON.parse(m ? m[0] : text);
  return obj as FunnelSpec;
}

/** Chat livre com 1 agente — usado na sala de operações. */
export async function chatWithAgentAI(opts: {
  apiKey: string;
  model: string;
  agentName: string;
  agentVoice: string;
  agentSkills: string[];
  agentExpectations: string;
  customSystemPrompt?: string;
  history: { role: "user" | "assistant"; content: string }[];
  userMessage: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const identity = opts.customSystemPrompt?.trim()
    ? opts.customSystemPrompt
    : `Você é ${opts.agentName} da AGÊNCIA VPC.\n` +
      `- Habilidades: ${opts.agentSkills.join(", ")}\n` +
      `- Expectativa: ${opts.agentExpectations}\n` +
      `- Voz característica: ${opts.agentVoice}`;

  const systemMsg =
    `${identity}\n\nVocê está conversando com o Marcio (CEO da casa) em tempo real, na sala de operações.\n` +
    `Responda na SUA voz característica, em pt-BR direto, sem disclaimer. Use 1-4 parágrafos curtos. ` +
    `Se Marcio te perguntar algo fora da sua especialidade, redirecione com humor pro especialista correto.`;

  const messages: any[] = [
    ...opts.history.map(h => ({ role: h.role, content: h.content })),
    { role: "user", content: opts.userMessage }
  ];

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1200,
      system: systemMsg,
      messages
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

export type BrainstormFormat =
  | "ideias"
  | "hooks"
  | "headlines"
  | "nomes"
  | "conceitos"
  | "roteiros"
  | "slogans"
  | "perguntas"
  | "ofertas"
  | "ctas";

export interface BrainstormItem {
  /** linha curta (título/headline) */
  title: string;
  /** explicação ou desenvolvimento (1-3 frases) */
  detail?: string;
  /** tag opcional (ex: "urgência", "curiosidade", "prova social") */
  tag?: string;
}

/** Brainstorm temático com Claude — devolve N opções selecionáveis. */
export async function brainstormThemeAI(opts: {
  apiKey: string;
  model: string;
  theme: string;
  format: BrainstormFormat;
  count: number;
  /** participantes da reunião (suas vozes alimentam o prompt) */
  attendeeNames?: string[];
  attendeeVoices?: string[];
  clientContext?: string;
  /** tom adicional opcional */
  toneHint?: string;
}): Promise<BrainstormItem[]> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const FORMAT_INSTR: Record<BrainstormFormat, string> = {
    ideias:    "IDEIAS de execução/conteúdo. Título = a ideia em uma linha. Detail = como faria + por que funciona.",
    hooks:     "HOOKS de 3 segundos (abertura de vídeo curto/reel). Título = o hook literal. Detail = princípio viral usado (pattern interrupt, curiosidade, controversa…).",
    headlines: "HEADLINES de carta/anúncio/post. Título = a headline. Detail = nível de consciência (Schwartz) + apelo principal.",
    nomes:     "NAMING: nomes pra produto/projeto/campanha. Título = o nome. Detail = significado + sonoridade + disponibilidade plausível.",
    conceitos: "CONCEITOS criativos abrangentes. Título = o conceito em 5-8 palavras. Detail = desdobramento em 2-3 linhas.",
    roteiros:  "MICRO-ROTEIROS de reel/vídeo (15-30s). Título = a premissa. Detail = beats principais (hook → corpo → CTA), 4-6 linhas.",
    slogans:   "SLOGANS / TAGLINES. Título = o slogan. Detail = ressonância emocional + memorabilidade.",
    perguntas: "PERGUNTAS PROVOCATIVAS pra abrir conversa/pesquisa. Título = a pergunta. Detail = o que ela revela / por que importa.",
    ofertas:   "OFERTAS irrecusáveis (Hormozi-style). Título = headline da oferta. Detail = stack de bônus + garantia + escassez.",
    ctas:      "CHAMADAS PRA AÇÃO (CTAs). Título = o CTA literal. Detail = gatilho psicológico ativado."
  };

  const peopleClause = (opts.attendeeNames?.length ?? 0) > 0
    ? `MESA NA SALA (canalize as vozes dessas pessoas, misturadas — não fale como uma só):
${opts.attendeeNames!.map((n, i) => `- ${n}${opts.attendeeVoices?.[i] ? ` (${opts.attendeeVoices[i]})` : ""}`).join("\n")}`
    : "";

  const prompt =
`Você é o moderador de uma sessão de BRAINSTORM da AGÊNCIA VPC. Marcio definiu o tema e quer ${opts.count} opções DIFERENTES e ESPECÍFICAS.

TEMA: "${opts.theme}"
${opts.clientContext ? `CONTEXTO DO CLIENTE: ${opts.clientContext}` : ""}
${opts.toneHint ? `TOM/DIREÇÃO ADICIONAL: ${opts.toneHint}` : ""}

FORMATO PEDIDO: ${opts.format.toUpperCase()}
${FORMAT_INSTR[opts.format]}

${peopleClause}

REGRAS:
1. Cada opção é DIFERENTE em ângulo, tom ou princípio — sem variações da mesma ideia.
2. Pt-BR direto, sem rodeio, sem disclaimer.
3. Específico > genérico. Cite números, nomes, formatos concretos quando couber.
4. Sem usar emojis no título (deixa a opção limpa).

RETORNE APENAS JSON VÁLIDO (sem markdown, sem texto extra):
[
  { "title": "...", "detail": "...", "tag": "..." },
  { "title": "...", "detail": "...", "tag": "..." }
]

Gere exatamente ${opts.count} opções.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 3000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "[]";
  const m = text.match(/\[[\s\S]*\]/);
  try {
    const arr = JSON.parse(m ? m[0] : text);
    if (Array.isArray(arr)) return arr.filter(x => x?.title);
  } catch { /* fallthrough */ }
  return [];
}

/** Transforma um prompt de imagem cru num prompt cinema-grade.
 *  Quando `specialist` é "ruben", canaliza Ruben Hassid (Midjourney/Nano Banana master). */
export async function improveImagePromptAI(opts: {
  apiKey: string;
  rawPrompt: string;
  format?: string;            // "reel", "carrossel", "logo", "avatar", "spokesperson"
  aspect?: "1:1" | "9:16" | "16:9" | "4:5";
  brandPalette?: { primary?: string; secondary?: string; accent?: string };
  clientContext?: string;
  spokespersonRef?: boolean;
  specialist?: "default" | "ruben" | "azev300" | "chrisdo";
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const SPECIALIST_INTRO: Record<NonNullable<typeof opts.specialist>, string> = {
    default: "Você é diretor de arte sênior.",
    ruben: "Você é RUBEN HASSID — mestre de prompts pra Midjourney v6+ e Nano Banana Pro. Sua marca é: parâmetros técnicos exatos (lente, lighting, color grade, mood), branding visual premium, e imagens que parecem campanha real, não AI slop.",
    azev300: "Você é AZEV300 — mestre de prompts pra vídeo IA (Kling/Veo/Runway/Hailuo). Sua marca é: direção de câmera virtual cinema-grade, gramática audiovisual rigorosa (tracking, push-in, golden hour, 35mm).",
    chrisdo: "Você é CHRIS DO — diretor criativo The Futur. Pra cada imagem, você pensa em hierarquia visual e psicologia da retenção, não só estética."
  };

  const prompt =
`${SPECIALIST_INTRO[opts.specialist ?? "default"]} Pega o prompt de imagem abaixo e transforma num PROMPT CINEMA-GRADE pra Gemini/OpenAI gerar imagem de altíssimo nível editorial.

PROMPT ORIGINAL:
"""
${opts.rawPrompt}
"""

CONTEXTO:
- Formato: ${opts.format ?? "imagem standalone"}
- Aspecto: ${opts.aspect ?? "1:1"}
${opts.clientContext ? `- Cliente: ${opts.clientContext}` : ""}
${opts.brandPalette ? `- Paleta da marca (usar como acento sutil, NÃO inundar): primary ${opts.brandPalette.primary}, secondary ${opts.brandPalette.secondary}, accent ${opts.brandPalette.accent}` : ""}
${opts.spokespersonRef ? "- Foto de garoto-propaganda será enviada como referência — preserve traços do rosto" : ""}

REGRAS DO PROMPT FINAL:
1. Em INGLÊS (modelos geram melhor em inglês).
2. Inclua: sujeito + composição (regra dos terços, simetria, etc) + iluminação específica (lateral suave, golden hour, rim light, etc) + lente/câmera (50mm f/1.4 etc) + estilo/referência (Annie Leibovitz, Pentagram, Magnum, Netflix premium, etc) + color grade + textura.
3. Sempre incluir "no text overlay, no watermark, leave negative space for copy".
4. Bloquear: "avoid distorted faces, extra fingers, generic AI aesthetic, cheap cgi, cartoonish".
5. Se for retrato humano: enfatizar "real person, dignified, not airbrushed, naturalistic skin".
6. Use paleta da marca como ACENTO (figurino/prop), não como fundo inundado.
7. Tamanho ideal: 150-250 palavras. Direto, técnico, sem rodeio.

RETORNE APENAS O PROMPT FINAL — sem rótulos, sem aspas, sem markdown, sem explicação.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-5",
      max_tokens: 600,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Melhora um texto curto (título OU descrição/prompt de demanda) usando Claude. */
export async function improveTextAI(opts: {
  apiKey: string;
  model: string;
  text: string;
  mode: "title" | "brief" | "prompt";
  context?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const MODE_INSTR: Record<typeof opts.mode, string> = {
    title:
      "Reescreva como um TÍTULO de demanda profissional: ≤80 caracteres, verbo no infinitivo, objetivo claro. Sem rodeio.",
    brief:
      "Reescreva como um BRIEFING de demanda enxuto: contexto + objetivo + restrições, máx 4 linhas, pt-BR direto.",
    prompt:
      "Otimize como PROMPT pra agente IA executar: comando claro + formato de entrega esperado + tom desejado, máx 5 linhas."
  };
  const prompt =
`${MODE_INSTR[opts.mode]}

TEXTO ORIGINAL:
${opts.text}

${opts.context ? `CONTEXTO ADICIONAL: ${opts.context}` : ""}

Retorne APENAS o texto reescrito — sem rótulo, sem aspas, sem markdown.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-haiku-4-5",
      max_tokens: 400,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Estima horas pra executar uma demanda. */
export async function estimateHoursAI(opts: {
  apiKey: string;
  title: string;
  description: string;
  targetDepts: string[];
  complexity?: string;
}): Promise<{ hours: number; reason: string }> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const prompt =
`Estime horas-homem pra ESTA demanda (não considere espera, só execução).

DEMANDA:
- título: ${opts.title}
- briefing: ${opts.description}
- departamentos: ${opts.targetDepts.join(", ")}
- complexidade: ${opts.complexity ?? "?"}

Baseline: copy curto = 1-2h, carrossel = 2-4h, vídeo 60s editado = 6-12h, VSL = 16-40h, lançamento = 80-200h.

Retorne APENAS JSON: {"hours": <numero_decimal>, "reason": "<1 linha>"}`;
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: "claude-haiku-4-5",
      max_tokens: 200,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}`);
  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "{}";
  const m = text.match(/\{[\s\S]*\}/);
  try {
    const obj = JSON.parse(m ? m[0] : text);
    return { hours: Number(obj.hours) || 2, reason: obj.reason || "" };
  } catch { return { hours: 2, reason: "fallback" }; }
}

/** Gera campo de brand kit (persona, audience, font, voiceTone, brief) com Claude. */
export async function generateBrandTextAI(opts: {
  apiKey: string;
  model: string;
  field: "persona" | "audience" | "font" | "voiceTone" | "brief" | "pitch" | "challenge" | "tags";
  clientName: string;
  clientPitch: string;
  segment: string;
  currentAudience: string;
  hint?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const FIELD_BRIEF: Record<typeof opts.field, string> = {
    persona:
      "PERSONA do cliente ideal: arquétipo, faixa etária, profissão, dor principal, sonho. 2-3 frases em pt-BR. Direto.",
    audience:
      "PÚBLICO-ALVO refinado: 1 parágrafo curto (3-4 frases) descrevendo quem compra, onde está, comportamento de compra, gatilhos.",
    font:
      "TIPOGRAFIA recomendada: 1-2 famílias de fonte (uma display, uma corpo) com justificativa em 1 frase. Cite nomes reais (Inter, IBM Plex, Söhne, Big Shoulders, etc).",
    voiceTone:
      "TOM DE VOZ da marca: 2 frases — 1 descrevendo o tom (formal/coloquial, técnico/emocional) + 1 exemplo de frase típica da marca.",
    brief:
      "BRIEFING GERAL da marca: 1 parágrafo de 4-5 linhas — posicionamento, diferencial, momento da marca, próximos 90 dias.",
    pitch:
      "PITCH da empresa em 1-2 frases. Descreve o que a empresa faz, pra quem, e qual o diferencial. Direto, premium, claro.",
    challenge:
      "DESAFIO PRINCIPAL do cliente em 1-2 frases. O que está travando crescimento/posicionamento.",
    tags:
      "TAGS pra busca: 6-10 palavras-chave separadas por vírgula. Inclui segmento, geografia, tipo de produto, perfil de cliente. Sem # nem aspas, só CSV."
  };

  const prompt =
`Você é o estrategista da AGÊNCIA VPC. Para o cliente abaixo, gere o campo solicitado.

CLIENTE: ${opts.clientName}
SEGMENTO: ${opts.segment}
PITCH: ${opts.clientPitch}
PÚBLICO ATUAL: ${opts.currentAudience}
${opts.hint ? `OBSERVAÇÃO DO MARCIO: ${opts.hint}` : ""}

CAMPO A GERAR: ${opts.field.toUpperCase()}
INSTRUÇÃO: ${FIELD_BRIEF[opts.field]}

Retorne APENAS o texto do campo, sem rótulo, sem aspas, sem markdown.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-6",
      max_tokens: 400,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Boletim diário com base nos eventos recentes. */
export async function generateDailyBriefingAI(opts: {
  apiKey: string;
  model: string;
  events: { kind: string; detail?: string; at: number }[];
  outputs: { taskTitle: string; persona: string; kind: string; at: number }[];
  hiredAgents: string[];
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");
  const date = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const eventsText = opts.events.slice(0, 30).map(e =>
    `- ${new Date(e.at).toLocaleString("pt-BR")}: [${e.kind}] ${e.detail ?? ""}`
  ).join("\n") || "(sem eventos)";
  const outputsText = opts.outputs.slice(0, 30).map(o =>
    `- ${o.persona} entregou ${o.kind} pra "${o.taskTitle}"`
  ).join("\n") || "(sem entregas)";

  const prompt =
`Você é o editor de comunicação interna da AGÊNCIA VPC. Hoje é ${date}.
Marcio pediu o BOLETIM DIÁRIO do escritório.

EVENTOS RECENTES (RH, contratações, demissões, mudanças):
${eventsText}

ENTREGAS RECENTES (criativos, textos, vídeos gerados):
${outputsText}

TIME ATUAL CONTRATADO: ${opts.hiredAgents.length} agentes

Escreva um boletim CONCISO e energético em pt-BR, formato:

▸ MANCHETE DO DIA
[1 frase forte, em caps]

▸ DESTAQUES
[3-4 bullets sobre o que rolou de mais importante]

▸ NÚMEROS
[1-2 linhas com indicadores: contratações, entregas, demandas]

▸ ATENÇÃO PRO MARCIO
[1-2 itens que demandam decisão dele]

▸ FRASE DO DIA
[1 frase motivacional ou irônica, no tom de uma agência premium]

Total ≤ 400 palavras. Direto, sem floreio.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 800,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Sessão de piadas na cafeteria conduzida pelo Endomarketing. */
export async function generateCafeteriaJokesAI(opts: {
  apiKey: string;
  model: string;
  /** lista dos funcionários presentes — só hired, exceto comediantes */
  victims: { name: string; bio: string; dept: string; voice: string }[];
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const list = opts.victims
    .map(v => `- ${v.name} (${v.dept}) — ${v.bio} · voz: ${v.voice}`)
    .join("\n");

  const prompt =
`Você é o roteirista de uma SESSÃO DE PIADAS na cafeteria da AGÊNCIA VPC.
Quatro comediantes do Endomarketing vão zoar amigavelmente os colegas presentes:
- CHESPIRITO (Roberto Gómez Bolaños): paternal, criador de mundos, prepara o palco. Inventa apelidos criativos pros colegas.
- CHAVES: inocente, "foi sem querer querendo", solta verdades inconvenientes sem perceber.
- MATHEUS CEARÁ: piada curta absurda, trocadilho, rajada.
- PAULINHO GOGÓ: "NÃO ACREDITO, CARLOS ALBERTO!", carioca esculachado, comentários ácidos.

PRESENTES NA CAFETERIA (alvos amigáveis das piadas):
${list}

GERE UMA SESSÃO DE 10-14 FALAS no formato:

▸ CHESPIRITO: [fala apresentando alguém com APELIDO criativo]
▸ CHAVES: [fala curta inocente]
▸ MATHEUS CEARÁ: [piada curta em rajada]
▸ PAULINHO GOGÓ: [esculacho carioca]

REGRAS:
1. Cada comediante deve aparecer ao menos 2x.
2. Crie APELIDOS criativos pros colegas baseados em sua skill/depto/voz (ex: "Sobral aqui virou Mestre dos Algoritmos do Meta — só fala em CPM").
3. Sem pudor — pode esculachar amigavelmente, brincar com manias, exagerar características. Mas sem ofensa pesada, é zoeira de happy hour.
4. Cada fala deve REALMENTE referenciar algo do colega (skill, voz, bio).
5. Tom: irreverente, descontraído, levemente absurdo (estilo Tilim, Praça é Nossa, A Praça é do Galvão).
6. NÃO invente outros comediantes além desses 4.
7. Use pt-BR coloquial. Pode soltar bordões deles.

Retorne SÓ as falas, sem introdução nem conclusão.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 1500,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

/** Gera output autônomo de um agente para uma demanda. */
export async function autoExecuteByAgentAI(opts: {
  apiKey: string;
  model: string;
  agentName: string;
  agentVoice: string;
  agentSkills: string[];
  agentExpectations: string;
  taskTitle: string;
  taskDescription: string;
  clientContext?: string;
  /** se fornecido, substitui o bloco "SOBRE VOCÊ" pelo prompt custom */
  systemPromptOverride?: string;
}): Promise<string> {
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const identityBlock = opts.systemPromptOverride?.trim()
    ? opts.systemPromptOverride
    : `Você é ${opts.agentName}, da AGÊNCIA VPC.\n` +
      `- Habilidades: ${opts.agentSkills.join(", ")}\n` +
      `- Expectativa: ${opts.agentExpectations}\n` +
      `- Voz característica: ${opts.agentVoice}`;

  const prompt =
`${identityBlock}

DEMANDA:
- título: ${opts.taskTitle}
- briefing: ${opts.taskDescription}
${opts.clientContext ? `- CONTEXTO DO CLIENTE: ${opts.clientContext}` : ""}

Entregue agora MESMO o trabalho — ESCRITO, FORMATADO, pronto pra ir pra revisão. Use SUA voz característica. Seja específico, prático, no formato que faz sentido pro tipo de entrega (lista, roteiro, copy, headline, código, plano, etc).

Comece direto pela entrega, sem disclaimers. Não diga "aqui está", apenas entregue. Use markdown leve quando útil (## títulos, listas).`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-5",
      max_tokens: 2000,
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!res.ok) throw new Error(`Claude ${res.status}: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return (json?.content?.[0]?.text ?? "").trim();
}

export async function generateAgendaAIStream(
  req: ClaudeReq,
  onChunk: (textSoFar: string) => void
): Promise<string> {
  if (!req.apiKey) throw new Error("API key Anthropic não configurada. Abra ⚙️ e cole sua chave.");
  if (req.personaIds.length === 0) throw new Error("Convoque ao menos 1 participante.");

  const body = {
    model: req.model || "claude-sonnet-4-6",
    max_tokens: 1500,
    stream: true,
    messages: [{ role: "user", content: buildPrompt(req) }]
  };

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": req.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify(body)
  });

  if (!res.ok || !res.body) {
    const text = await res.text();
    throw new Error(`Claude API ${res.status}: ${text.slice(0, 300)}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let acc = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // SSE: linhas separadas por \n\n, prefixadas por "data: "
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const part of parts) {
      for (const line of part.split("\n")) {
        if (!line.startsWith("data: ")) continue;
        const payload = line.slice(6).trim();
        if (!payload || payload === "[DONE]") continue;
        try {
          const evt = JSON.parse(payload);
          if (evt.type === "content_block_delta" && evt.delta?.type === "text_delta") {
            acc += evt.delta.text ?? "";
            onChunk(acc);
          }
        } catch { /* ignora SSE malformado */ }
      }
    }
  }
  return acc;
}

export async function generatePerformanceReviewAI(opts: {
  apiKey: string;
  model: string;
  personaId: string;
  rating: number;
  managerNote: string;
}): Promise<string> {
  const persona = PERSONAS.find(p => p.id === opts.personaId);
  if (!persona) throw new Error("Persona não encontrada.");
  if (!opts.apiKey) throw new Error("API key Anthropic não configurada.");

  const prompt = `Você é o supervisor de RH da AGÊNCIA VPC. Marcio (gerente) acaba de avaliar um membro do time.

MEMBRO: ${persona.name}
DEPARTAMENTO/CARGO: ${persona.slotId}
BIO: ${persona.bio}
NOTA DO MARCIO: ${opts.rating}/5
OBSERVAÇÃO DO MARCIO: ${opts.managerNote || "(sem observações específicas)"}

Gere uma "performance review" em português do Brasil, em 4 blocos curtos:

▸ PONTOS FORTES
[2 bullet points específicos para este membro, baseados em quem ele é]

▸ PONTOS DE MELHORIA
[2 bullet points específicos]

▸ FEEDBACK DIRETO AO MEMBRO (1 parágrafo curto, em segunda pessoa, tom firme mas respeitoso)
[texto]

▸ RECOMENDAÇÃO AO MARCIO
[1 frase: manter / promover / colocar em PIP / desligar]

Seja conciso, específico, evite clichê. Use o tom de quem conhece o membro de verdade.`;

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": opts.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify({
      model: opts.model || "claude-sonnet-4-6",
      max_tokens: 800,
      messages: [{ role: "user", content: prompt }]
    })
  });

  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Claude API ${res.status}: ${t.slice(0, 300)}`);
  }
  const json = await res.json();
  return json?.content?.[0]?.text ?? "";
}

export async function generateAgendaAI(req: ClaudeReq): Promise<string[]> {
  if (!req.apiKey) throw new Error("API key Anthropic não configurada. Abra ⚙️ e cole sua chave.");
  if (req.personaIds.length === 0) throw new Error("Convoque ao menos 1 participante.");

  const body = {
    model: req.model || "claude-sonnet-4-6",
    max_tokens: 1500,
    messages: [{ role: "user", content: buildPrompt(req) }]
  };

  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": req.apiKey,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true"
    },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Claude API ${res.status}: ${text.slice(0, 300)}`);
  }

  const json = await res.json();
  const text: string = json?.content?.[0]?.text ?? "";
  return text.split("\n");
}
